'use client';
import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Download, X, RefreshCw, Phone } from 'lucide-react';

interface Booking {
  id: string;
  ritual: string;
  priest: string;
  priestPhone: string;
  date: string;
  time: string;
  address: string;
  status: 'confirmed' | 'pending';
  price: string;
  duration: string;
  emoji: string;
}

const upcomingBookings: Booking[] = [
  {
    id: 'YGN-2026-001',
    ritual: 'Ganesh Puja',
    priest: 'Pandit Ramesh Kumar',
    priestPhone: '+91 98765 43210',
    date: 'Aug 7, 2026',
    time: '9:00 AM',
    address: '12, Lotus Lane, Koramangala, Bengaluru',
    status: 'confirmed',
    price: '₹2,500',
    duration: '2 hours',
    emoji: '🐘',
  },
  {
    id: 'YGN-2026-002',
    ritual: 'Satyanarayana Puja',
    priest: 'Pandit Suresh Sharma',
    priestPhone: '+91 87654 32109',
    date: 'Aug 15, 2026',
    time: '7:30 AM',
    address: '45, Tulsi Nagar, Indiranagar, Bengaluru',
    status: 'pending',
    price: '₹3,200',
    duration: '3 hours',
    emoji: '🪔',
  },
];

type ModalType = 'cancel' | 'reschedule' | 'contact' | null;

export default function UpcomingRituals() {
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [actionDone, setActionDone] = useState<string | null>(null);

  const openModal = (booking: Booking, type: ModalType) => {
    setSelectedBooking(booking);
    setModalType(type);
    setActionDone(null);
  };

  const closeModal = () => {
    setSelectedBooking(null);
    setModalType(null);
    setRescheduleDate('');
    setRescheduleTime('');
  };

  const handleDownload = (booking: Booking) => {
    const content = `BOOKING CONFIRMATION\n\nBooking ID: ${booking.id}\nRitual: ${booking.ritual}\nPriest: ${booking.priest}\nDate: ${booking.date}\nTime: ${booking.time}\nAddress: ${booking.address}\nDuration: ${booking.duration}\nAmount: ${booking.price}\nStatus: ${booking.status.toUpperCase()}\n\nThank you for booking with Yagnick.\nFor support: support@yagnick.com`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Booking-${booking.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="font-display text-xl font-bold text-maroon">Upcoming Rituals</h2>
        <p className="text-sm text-maroon/50 mt-0.5">{upcomingBookings.length} scheduled bookings</p>
      </div>

      {upcomingBookings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gold/15 p-10 text-center">
          <div className="text-4xl mb-3">🪔</div>
          <p className="text-maroon font-semibold">No upcoming rituals</p>
          <p className="text-sm text-maroon/50 mt-1">Book your next puja to see it here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {upcomingBookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gold/10 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-saffron/10 flex items-center justify-center text-xl flex-shrink-0">
                  {booking.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-maroon">{booking.ritual}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      booking.status === 'confirmed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                    }`}>
                      {booking.status === 'confirmed' ? '✓ Confirmed' : '⏳ Pending'}
                    </span>
                  </div>
                  <p className="text-xs text-maroon/50 mt-0.5">Booking ID: {booking.id}</p>
                </div>
              </div>

              {/* Details */}
              <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2.5">
                  <Calendar size={14} className="text-saffron mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-maroon/40 font-medium">Date & Time</p>
                    <p className="text-sm text-maroon font-medium">{booking.date} · {booking.time}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock size={14} className="text-saffron mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-maroon/40 font-medium">Duration</p>
                    <p className="text-sm text-maroon font-medium">{booking.duration}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 sm:col-span-2">
                  <MapPin size={14} className="text-saffron mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-maroon/40 font-medium">Location</p>
                    <p className="text-sm text-maroon font-medium">{booking.address}</p>
                  </div>
                </div>
              </div>

              {/* Priest + Price */}
              <div className="px-5 py-3 bg-cream/40 border-t border-gold/10 flex items-center justify-between">
                <div>
                  <p className="text-xs text-maroon/40">Priest</p>
                  <p className="text-sm font-semibold text-maroon">{booking.priest}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-maroon/40">Amount</p>
                  <p className="text-sm font-bold text-maroon">{booking.price}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gold/10 flex flex-wrap gap-2">
                <button
                  onClick={() => openModal(booking, 'reschedule')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-saffron border border-saffron/30 px-3 py-1.5 rounded-full hover:bg-saffron/5 transition-colors"
                >
                  <RefreshCw size={12} /> Reschedule
                </button>
                <button
                  onClick={() => openModal(booking, 'cancel')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-red-500 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-50 transition-colors"
                >
                  <X size={12} /> Cancel
                </button>
                <button
                  onClick={() => handleDownload(booking)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-maroon/60 border border-gold/20 px-3 py-1.5 rounded-full hover:bg-cream transition-colors"
                >
                  <Download size={12} /> Download
                </button>
                <button
                  onClick={() => openModal(booking, 'contact')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-maroon/60 border border-gold/20 px-3 py-1.5 rounded-full hover:bg-cream transition-colors"
                >
                  <Phone size={12} /> Contact Priest
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalType && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-scale-in">
            {/* Cancel Modal */}
            {modalType === 'cancel' && (
              <>
                <div className="px-5 py-4 border-b border-gold/10 flex items-center justify-between">
                  <h3 className="font-semibold text-maroon">Cancel Booking</h3>
                  <button onClick={closeModal}><X size={18} className="text-maroon/40" /></button>
                </div>
                {actionDone ? (
                  <div className="px-5 py-8 text-center">
                    <div className="text-3xl mb-2">✓</div>
                    <p className="font-semibold text-maroon">Booking Cancelled</p>
                    <p className="text-sm text-maroon/50 mt-1">Your booking has been cancelled.</p>
                    <button onClick={closeModal} className="mt-4 text-sm text-saffron font-medium">Close</button>
                  </div>
                ) : (
                  <div className="px-5 py-5 space-y-4">
                    <p className="text-sm text-maroon/70">Are you sure you want to cancel <strong>{selectedBooking.ritual}</strong> on {selectedBooking.date}?</p>
                    <p className="text-xs text-maroon/40 bg-amber-50 rounded-lg px-3 py-2">Cancellations made 24+ hours before the ritual are eligible for a full refund.</p>
                    <div className="flex gap-2">
                      <button onClick={closeModal} className="flex-1 text-sm font-medium text-maroon border border-gold/20 py-2 rounded-xl hover:bg-cream transition-colors">Keep Booking</button>
                      <button onClick={() => setActionDone('cancelled')} className="flex-1 text-sm font-semibold text-white bg-red-500 py-2 rounded-xl hover:bg-red-600 transition-colors">Yes, Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Reschedule Modal */}
            {modalType === 'reschedule' && (
              <>
                <div className="px-5 py-4 border-b border-gold/10 flex items-center justify-between">
                  <h3 className="font-semibold text-maroon">Reschedule Booking</h3>
                  <button onClick={closeModal}><X size={18} className="text-maroon/40" /></button>
                </div>
                {actionDone ? (
                  <div className="px-5 py-8 text-center">
                    <div className="text-3xl mb-2">✓</div>
                    <p className="font-semibold text-maroon">Reschedule Requested</p>
                    <p className="text-sm text-maroon/50 mt-1">We'll confirm the new date with your priest.</p>
                    <button onClick={closeModal} className="mt-4 text-sm text-saffron font-medium">Close</button>
                  </div>
                ) : (
                  <div className="px-5 py-5 space-y-4">
                    <p className="text-sm text-maroon/70">Select a new date and time for <strong>{selectedBooking.ritual}</strong>.</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-maroon/60 block mb-1">New Date</label>
                        <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                          className="w-full text-sm text-maroon border border-gold/20 rounded-xl px-3 py-2 outline-none focus:border-saffron/40 bg-cream/30" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-maroon/60 block mb-1">Preferred Time</label>
                        <select value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}
                          className="w-full text-sm text-maroon border border-gold/20 rounded-xl px-3 py-2 outline-none focus:border-saffron/40 bg-cream/30">
                          <option value="">Select time</option>
                          <option>6:00 AM</option><option>7:00 AM</option><option>8:00 AM</option>
                          <option>9:00 AM</option><option>10:00 AM</option><option>4:00 PM</option>
                          <option>5:00 PM</option><option>6:00 PM</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={closeModal} className="flex-1 text-sm font-medium text-maroon border border-gold/20 py-2 rounded-xl hover:bg-cream transition-colors">Cancel</button>
                      <button
                        onClick={() => rescheduleDate && rescheduleTime && setActionDone('rescheduled')}
                        className={`flex-1 text-sm font-semibold text-white py-2 rounded-xl transition-colors ${rescheduleDate && rescheduleTime ? 'bg-saffron hover:bg-saffron/90' : 'bg-saffron/40 cursor-not-allowed'}`}
                      >
                        Request Reschedule
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Contact Priest Modal */}
            {modalType === 'contact' && (
              <>
                <div className="px-5 py-4 border-b border-gold/10 flex items-center justify-between">
                  <h3 className="font-semibold text-maroon">Contact Priest</h3>
                  <button onClick={closeModal}><X size={18} className="text-maroon/40" /></button>
                </div>
                <div className="px-5 py-5 space-y-4">
                  <div className="flex items-center gap-3 bg-cream/50 rounded-xl p-3">
                    <div className="w-10 h-10 rounded-full saffron-gradient flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {selectedBooking.priest.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-maroon">{selectedBooking.priest}</p>
                      <p className="text-xs text-maroon/50">{selectedBooking.priestPhone}</p>
                    </div>
                  </div>
                  <p className="text-xs text-maroon/50">You can reach your priest directly for any queries about your upcoming ritual.</p>
                  <div className="flex gap-2">
                    <a href={`tel:${selectedBooking.priestPhone}`}
                      className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-saffron py-2.5 rounded-xl hover:bg-saffron/90 transition-colors">
                      <Phone size={15} /> Call Now
                    </a>
                    <button onClick={closeModal} className="flex-1 text-sm font-medium text-maroon border border-gold/20 py-2.5 rounded-xl hover:bg-cream transition-colors">Close</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
